import { getHttpInstance } from '../../services/defaultHttpService';
import {
  REQUEST_ID_HEADER,
  STATUS,
  TIMEOUT_EXTENDED
} from '../../constants/commonConstants';
import * as types from './constants';
import {
  CATEGORY_GET_DETAILS_BEGIN,
  CATEGORY_GET_DETAILS_SUCCESS,
  CATEGORY_GET_DETAILS_FAIL
} from '../categories/constants';
import { isDev, isEmpty } from '@app/utils';
import {
  createURLWithParams,
  getParamsForReduxFromHash,
  getUnitOfMeasure,
  handlePLPDetailsResults,
  normalizeCategoryDetails
} from './utils';
import { ERROR_DOMAIN, ERROR_PATH } from '../../constants/errorConstants';
import { clearError, setError } from '../exception/actions';
import { normalizeError } from '../exception/utils';
import queryString from 'query-string';
import { setCategoryData, updateCategoryProducts } from '../categories/actions';
import { setProductList, updateProductList } from '../products/actions';
import { extractProductsIds } from '../products/utils';
import { filteredProductsCardsArr } from '@app/utils/products';
import { v4 } from 'uuid';

const timeout = isDev() ? TIMEOUT_EXTENDED : undefined;

export const getPlpDetails =
  (queryParamsObject = {}, { facetsLoading = false } = {}) =>
  async (dispatch, getState) => {
    const http = getHttpInstance(timeout);
    const state = getState();
    const isEquipmentSelected =
      !!state.myEquipment.equipments?.selectedEquipment?.uniqueIndex;

    const {
      hash,
      history,
      search = '',
      url,
      offset = 0,
      ...rest
    } = queryParamsObject;
    const {
      getPlpDetailsError,
      sortBy,
      getPlpDetailsStatus,
      current: category,
      unitOfMeasure: defaultPlpUom
    } = state.plpUI;
    const { defaultUnitOfMeasure: dealerUnitOfMeasure } = state.common ?? {};
    const isFirstCall = getPlpDetailsStatus === STATUS.IDLE;
    const parsedHashRaw = queryString.parse(hash);
    // need to parse string to boolean if it exists
    const nonFalseFilterByEquipment = isFirstCall
      ? parsedHashRaw.filterByEquipment ?? true
      : parsedHashRaw.filterByEquipment;
    const parsedHash = {
      ...parsedHashRaw,
      ...(parsedHashRaw.filterByEquipment || isFirstCall
        ? {
            filterByEquipment:
              parsedHashRaw.filterByEquipment !== 'false'
                ? nonFalseFilterByEquipment
                : false
          }
        : {})
    };
    const lowestLoadedPage = offset + 1;

    const shoppingPreferencesUom =
      state.shoppingPreferences?.shoppingPreferencesData?.uom;
    const urlWithParams = createURLWithParams(
      state,
      { ...rest, offset },
      parsedHash
    );
    if (getPlpDetailsError) {
      dispatch(clearError(ERROR_DOMAIN.PLP, ERROR_PATH.MAIN));
    }

    if (isFirstCall) {
      dispatch({ type: CATEGORY_GET_DETAILS_BEGIN });
    }

    dispatch({ type: types.PLP_GET_DETAILS_BEGIN });
    facetsLoading &&
      dispatch({ type: types.PLP_SET_FACETS_LOADING, payload: facetsLoading });
    try {
      const { data = {} } = await http.get(urlWithParams, {
        headers: {
          [REQUEST_ID_HEADER]: v4()
        }
      });
      if (isFirstCall) {
        await dispatch({
          type: CATEGORY_GET_DETAILS_SUCCESS,
          payload: { category, data }
        });
      }

      const unitOfMeasure = getUnitOfMeasure({
        defaultPlpUom,
        dealerUnitOfMeasure,
        shoppingPreferencesUom
      });

      let payload = { lowestLoadedPage, unitOfMeasure };
      if (!isEmpty(parsedHash)) {
        const params = getParamsForReduxFromHash({ parsedHash, data });
        await dispatch({
          type: types.PLP_SET_QUERY_PARAM,
          payload: params
        });
      }
      const { products, ...rest } = data;
      const extractedProducts = extractProductsIds(products);
      await dispatch(
        setCategoryData({
          data: normalizeCategoryDetails(
            {
              ...rest,
              productsId: extractedProducts,
              id: category
            },
            false
          ),
          category
        })
      );

      const filterByEquipment = isEquipmentSelected
        ? !!parsedHash.filterByEquipment
        : undefined;
      // handle url update based on active filters
      if (history) {
        const updatedHash = {
          filterByEquipment,
          sortBy: parsedHash.sortBy ?? sortBy,
          facets: parsedHash.facets,
          scrollToProduct: parsedHash?.scrollToProduct
        };
        history.replace(
          `${url}${search}#${queryString.stringify(updatedHash)}`
        );
      }
      await dispatch(setProductList(products ?? []));
      await dispatch({ type: types.PLP_GET_DETAILS_SUCCESS, payload });

      if (!isEmpty(products)) {
        return await handlePLPDetailsResults(getState, dispatch, data);
      }
    } catch (error) {
      const err = normalizeError(error);
      if (isFirstCall) {
        dispatch({ type: CATEGORY_GET_DETAILS_FAIL });
        dispatch(setError(ERROR_DOMAIN.CATEGORY, ERROR_PATH.MAIN, err));
      }
      dispatch({ type: types.PLP_GET_DETAILS_FAIL, payload: err });
      dispatch(setError(ERROR_DOMAIN.PLP, ERROR_PATH.MAIN, err));
    } finally {
      facetsLoading &&
        dispatch({ type: types.PLP_SET_FACETS_LOADING, payload: false });
    }
  };

export const getMorePlpDetails =
  (queryParamsObject = {}) =>
  async (dispatch, getState) => {
    const http = getHttpInstance(timeout);
    const state = getState();
    const urlWithParams = createURLWithParams(state, queryParamsObject);
    const { getMorePlpDetailsError, current: category } = state.plpUI;
    const currentProductsIds =
      state.categories.byId[category]?.productsId ?? [];
    if (getMorePlpDetailsError) {
      dispatch(clearError(ERROR_DOMAIN.PLP, ERROR_PATH.MAIN));
    }
    dispatch({ type: types.PLP_GET_MORE_DETAILS_BEGIN });
    try {
      const { data = {} } = await http.get(urlWithParams, {
        headers: {
          [REQUEST_ID_HEADER]: v4()
        }
      });

      const { products } = data;
      const nextProductsIds = extractProductsIds(products);

      dispatch(
        updateCategoryProducts({
          productsId: [...currentProductsIds, ...nextProductsIds],
          category
        })
      );
      dispatch(updateProductList(products ?? []));
      await dispatch({ type: types.PLP_GET_MORE_DETAILS_SUCCESS });

      if (!isEmpty(products)) {
        return await handlePLPDetailsResults(getState, dispatch, data);
      }
    } catch (error) {
      const err = normalizeError(error);
      dispatch({ type: types.PLP_GET_MORE_DETAILS_FAIL, payload: err });
      dispatch(setError(ERROR_DOMAIN.PLP, ERROR_PATH.MAIN, err));
    }
  };

export const getPreviousPlpDetails =
  (queryParamsObject = {}) =>
  async (dispatch, getState) => {
    const http = getHttpInstance(timeout);
    const state = getState();

    const lowestLoadedPage = queryParamsObject.offset + 1;
    const urlWithParams = createURLWithParams(state, queryParamsObject);
    const { getPrevPlpDetailsError, current: category } = state.plpUI;
    const currentProductsIds =
      state.categories.byId[category]?.productsId ?? [];
    if (getPrevPlpDetailsError) {
      dispatch(clearError(ERROR_DOMAIN.PLP, ERROR_PATH.MAIN));
    }
    dispatch({ type: types.PLP_GET_PREVIOUS_DETAILS_BEGIN });
    try {
      const { data = {} } = await http.get(urlWithParams, {
        headers: {
          [REQUEST_ID_HEADER]: v4()
        }
      });
      const payload = {
        lowestLoadedPage
      };

      const { products } = data;
      const prevProductsIds = extractProductsIds(products);

      dispatch(
        updateCategoryProducts({
          productsId: [...prevProductsIds, ...currentProductsIds],
          category
        })
      );
      dispatch(updateProductList(products ?? []));
      await dispatch({ type: types.PLP_GET_PREVIOUS_DETAILS_SUCCESS, payload });

      if (!isEmpty(products)) {
        return await handlePLPDetailsResults(getState, dispatch, data);
      }
    } catch (error) {
      const err = normalizeError(error);
      dispatch({ type: types.PLP_GET_PREVIOUS_DETAILS_FAIL, payload: err });
      dispatch(setError(ERROR_DOMAIN.PLP, ERROR_PATH.MAIN, err));
    }
  };

export const setPlpCurrent = data => ({
  type: types.PLP_GET_CURRENT_CATEGORY,
  payload: data
});

export const setPLPQueryParam =
  (params, fetchPlpDetails = true) =>
  async dispatch => {
    await dispatch({
      type: types.PLP_SET_QUERY_PARAM,
      payload: { ...params }
    });
    if (fetchPlpDetails) {
      return dispatch(
        getPlpDetails({}, { facetsLoading: !Object.hasOwn(params, 'sortBy') }) //Check if it's a sort action and avoid the loader
      );
    }
  };

export const setPlpEquipmentChecked = payload => async dispatch => {
  await dispatch({
    type: types.PLP_SET_EQUIPMENT_CHECKED,
    payload
  });
  return dispatch(getPlpDetails({}, { facetsLoading: true }));
};

export const setPLPProductCardExpanded = payload => (dispatch, getState) => {
  const state = getState();
  const { productCardExpandedArr = [] } = state.plpUI;
  return dispatch({
    type: types.PLP_SET_PRODUCT_CARD_EXPANDED,
    payload: filteredProductsCardsArr(productCardExpandedArr, payload)
  });
};

export const setBucketModelActiveFacet = payload => async dispatch => {
  await dispatch({
    type: types.PLP_SET_BUCKET_MODEL_ACTIVE_FACET,
    payload
  });
};
